import { Box, Typography } from '@cambridgeassessment/cambridge-ui';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { PASSWORD_RULES, SOC_USER_PASSWORD_SUMMARY } from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import PasswordField from '../PasswordField/PasswordField';
import { useStyles } from './styles';
import ValidationItem from './ValidationItem';

export type Validation = 'valid' | 'invalid' | 'unknown';

type Props = {
  password: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setIsValid: (isValid: boolean) => void;
};

const CreatePasswordField: React.FC<Props> = ({
  password,
  onChange,
  setIsValid,
}) => {
  const [isLenghtValid, setIsLenghtValid] = useState<Validation>('unknown');
  const [areLettersValid, setAreLettersValid] = useState<Validation>('unknown');
  const [areNumbersValid, setAreNumbersValid] = useState<Validation>('unknown');

  const {
    user: { hasPassword },
  } = useAuth();

  useEffect(() => {
    const isValid =
      isLenghtValid === 'valid' &&
      areLettersValid === 'valid' &&
      areNumbersValid === 'valid';

    setIsValid(isValid);
  }, [password]);

  const classes = useStyles();

  const validateLength = useCallback((value: string) => {
    const isValid = value.length >= PASSWORD_RULES.length;
    setIsLenghtValid(isValid ? 'valid' : 'invalid');
  }, []);

  const validateLetters = (value: string) => {
    const matching = value.match(/[A-Z,a-z]+/g);
    const areValid = matching && matching.length >= PASSWORD_RULES.letters;
    setAreLettersValid(areValid ? 'valid' : 'invalid');
  };

  const validateNumbers = (value: string) => {
    const matching = value.match(/\d/g);
    const areValid = matching && matching.length >= PASSWORD_RULES.numbers;
    setAreNumbersValid(areValid ? 'valid' : 'invalid');
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);
    validateLength(event.target.value);
    validateLetters(event.target.value);
    validateNumbers(event.target.value);
  };

  return (
    <Box>
      <Box mb={3}>
        {!hasPassword && (
          <Typography
            variant="body1"
            className={classes.listTitle}
            data-testid="socialSummary"
          >
            {SOC_USER_PASSWORD_SUMMARY}
          </Typography>
        )}
      </Box>
      <Typography
        variant="h5"
        className={classes.title}
        data-testid="passwordField-label"
      >
        Create password
      </Typography>
      <Typography
        variant="body1"
        className={classes.listTitle}
        data-testid="descriptionTitle"
      >
        Your password must include:
      </Typography>
      <ul className={classes.list}>
        <ValidationItem
          validation={isLenghtValid}
          text="Eight (8) characters or more"
        />
        <ValidationItem
          validation={areLettersValid}
          text="One (1) letter or more"
        />
        <ValidationItem
          validation={areNumbersValid}
          text="One (1) number or more"
        />
      </ul>
      <PasswordField password={password} onChange={handleChange} label="" />
    </Box>
  );
};

export default memo(CreatePasswordField);
